import { useCallback } from 'react'
import { splitSignature } from '@ethersproject/bytes'
import { useAccount, useSignMessage, useSignTypedData } from 'wagmi'
import { SignMessageArgs, SignTypedDataArgs, TypedData } from '../types'

export function useSign() {
  const { address } = useAccount()
  const { signMessageAsync } = useSignMessage()

  return {
    signMessage: useCallback(
      async (args: SignMessageArgs) => {
        if (!address) {
          throw new Error('No account connected')
        }

        // Check for Binance Chain wallet - note: connector.id may not be 'bsc' in AppKit
        if (window.BinanceChain && typeof window.BinanceChain.bnbSign === 'function') {
          try {
            const res = await window.BinanceChain.bnbSign(address, args.message as string)

            if (res) {
              return splitSignature(res.signature)
            }
          } catch (error) {
            console.error('Binance Chain sign error:', error)
            // Fall through to regular signMessage if Binance Chain sign fails
          }
        }

        const res = await signMessageAsync({
          account: address,
          message: args.message,
        })

        return splitSignature(res)
      },
      [address, signMessageAsync],
    ),
  }
}

export function useTypedDataSign() {
  const { address } = useAccount()
  const { signTypedDataAsync } = useSignTypedData()

  return {
    signTypedData: useCallback(
      async (args: SignTypedDataArgs<TypedData>) => {
        if (!address) {
          throw new Error('No account connected')
        }

        const res = await signTypedDataAsync({
          account: address,
          domain: args.domain,
          types: args.types,
          primaryType: args.primaryType,
          message: args.message,
        })

        return splitSignature(res)
      },
      [address, signTypedDataAsync],
    ),
  }
}
